import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { createRole, listPermissions, listRoles, type RoleCreate } from "@/api/client";
import { useSession } from "@/auth/session";
import { Field, OtherProblems, inputClass, serverProblems, submitClass } from "@/components/form";
import { EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { groupPermissions, holderCount } from "@/features/roles/permissions";
import { ScrollArea } from "@/components/ScrollArea";

export function SystemBadge() {
  return <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600">System</span>;
}

export function RoleMatrix() {
  const { can } = useSession();
  const roles = useQuery({ queryKey: ["roles"], queryFn: listRoles });
  const permissions = useQuery({ queryKey: ["permissions"], queryFn: listPermissions });
  const [creating, setCreating] = useState(false);
  const groups = groupPermissions(permissions.data ?? []);
  const columns = roles.data ?? [];

  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title="Roles"
        detail="Which permissions each role grants; open a role to change it"
        action={
          can("role.manage") ? (
            <ToolbarButton disabled={creating} onClick={() => setCreating(true)}>
              New role
            </ToolbarButton>
          ) : null
        }
      />
      <ErrorBanner error={roles.error ?? permissions.error} />
      {creating ? <NewRole onCancel={() => setCreating(false)} /> : null}

      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        {roles.isLoading || permissions.isLoading ? (
          <EmptyState title="Loading roles" detail="Fetching roles and permissions." />
        ) : roles.isSuccess && permissions.isSuccess ? (
          <ScrollArea label="Permission matrix">
            <table aria-label="Permission matrix" className="w-full text-left text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-xs text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium uppercase">Permission</th>
                  {columns.map((role) => (
                    <th key={role.id} className="px-3 py-3 text-center align-bottom font-medium">
                      <Link
                        to="/roles/$roleId"
                        params={{ roleId: role.id }}
                        className="block text-sm text-zinc-950 underline-offset-2 hover:underline"
                      >
                        {role.name}
                      </Link>
                      <div className="mt-1 flex flex-col items-center gap-1">
                        {role.is_system ? <SystemBadge /> : null}
                        <span>{holderCount(role.user_count)}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              {groups.map(({ group, permissions: items }) => (
                <tbody key={group} aria-label={group} className="divide-y divide-zinc-100 border-b border-zinc-200">
                  <tr className="bg-zinc-50">
                    <th colSpan={columns.length + 1} scope="colgroup" className="px-4 py-2 text-xs font-semibold uppercase text-zinc-600">
                      {group}
                    </th>
                  </tr>
                  {items.map((permission) => (
                    <tr key={permission.key}>
                      <th scope="row" className="px-4 py-2 font-normal">
                        <div className="font-mono text-xs text-zinc-900">{permission.key}</div>
                        <div className="text-xs text-zinc-500">{permission.description}</div>
                      </th>
                      {columns.map((role) => (
                        <td key={role.id} className="px-3 py-2 text-center">
                          {role.permissions.includes(permission.key) ? (
                            <>
                              <span aria-hidden="true" className="text-emerald-700">
                                ✓
                              </span>
                              <span className="sr-only">granted</span>
                            </>
                          ) : null}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </ScrollArea>
        ) : null}
      </div>
    </section>
  );
}

function NewRole({ onCancel }: { onCancel: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const create = useMutation({
    mutationFn: (body: RoleCreate) => createRole(body),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.setQueryData(["role", created.id], created);
      // A new role grants nothing; its page is where permissions are chosen.
      await navigate({ to: "/roles/$roleId", params: { roleId: created.id } });
    },
  });
  const edited = () => {
    if (create.error) create.reset();
  };
  const server = serverProblems(create.error, ["name", "description"]);
  const blocked = name.trim() === "";

  return (
    <section aria-labelledby="new-role" className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 id="new-role" className="text-sm font-semibold">
        New role
      </h2>
      <form
        noValidate
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (blocked || create.isPending) return;
          const body: RoleCreate = { name: name.trim() };
          if (description.trim() !== "") body.description = description.trim();
          create.mutate(body);
        }}
      >
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Name" problem={server.byField.name}>
            {(props) => (
              <input
                {...props}
                autoFocus
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  edited();
                }}
                className={inputClass}
              />
            )}
          </Field>
          <Field label="Description" problem={server.byField.description}>
            {(props) => (
              <input
                {...props}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  edited();
                }}
                className={inputClass}
              />
            )}
          </Field>
        </div>
        <ErrorBanner error={create.error} />
        <OtherProblems lines={server.other} />
        <div className="flex flex-wrap gap-2">
          <button type="submit" disabled={blocked || create.isPending} className={submitClass}>
            {create.isPending ? "Creating..." : "Create role"}
          </button>
          <ToolbarButton disabled={create.isPending} onClick={onCancel}>
            Cancel
          </ToolbarButton>
        </div>
      </form>
    </section>
  );
}
